import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bot, X } from 'lucide-react';
import { sendChatMessage } from '@/services/chatbot';

const ChatWidget = ({ variant = 'page', onClose }) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (message = input) => {
    if (!message || loading) return;
    setLoading(true);
    setError('');
    setShowSuggestions(false);
    try {
      const result = await sendChatMessage(message, conversationId);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: message },
        { role: 'assistant', content: result.reply },
      ]);
      setConversationId(result.conversationId);
      setInput('');
    } catch (err) {
      setError(err.message || 'Failed to reach the Nyinimu assistant.');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Show Properties', message: 'Show me available properties' },
    { label: 'Book Visit', message: 'I want to schedule a property visit' },
    { label: 'Contact Info', message: 'How can I contact Nyinimu?' },
    { label: 'Read Blog', message: 'Show me recent blog posts' },
  ];

  const isInline = variant === 'inline';

  const wrapperClass = isInline
    ? 'flex flex-col bg-white rounded-2xl shadow-2xl border border-stone-100 w-[320px] sm:w-[360px] h-[520px] p-4'
    : 'bg-white rounded-lg shadow p-4 mb-4 h-[60vh] flex flex-col';

  return (
    <div className={wrapperClass}>
      {isInline && (
        <div className="flex items-center justify-between pb-3 border-b border-stone-100 mb-3">
          <div>
            <p className="text-sm font-semibold text-stone-800 flex items-center gap-2">
              <Bot className="w-8 h-8 text-[#81be00]" />
              Nyinimu Assistant
            </p>
            <p className="text-xs text-stone-500">Ask about homes, payments, or bookings.</p>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="p-1 rounded-full hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-hidden" ref={containerRef}>
        <div className="h-full overflow-auto space-y-3 pr-1">
          {messages.length === 0 && showSuggestions && (
            <div className="space-y-3">
              <div className="text-stone-500 text-sm leading-relaxed bg-stone-50 border border-dashed rounded-lg p-3">
                Hi! I'm the Nyinimu AI Assistant. How can I help you today?
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(action.message)}
                    className="text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-2 rounded-lg transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div
              key={`${msg.role}-${idx}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm leading-snug ${
                  msg.role === 'user'
                    ? 'bg-[#e6ffcc] text-stone-800 rounded-br-sm'
                    : 'bg-stone-800 text-white rounded-bl-sm'
                }`}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-stone-800 text-white px-4 py-2 rounded-2xl rounded-bl-sm text-sm">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  Thinking...
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3">
        <textarea
          className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#81be00]/50 focus:border-[#81be00] resize-none"
          rows={isInline ? 3 : 2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Type your question here... (Press Enter to send)"
        />
        <div className="flex items-center justify-between mt-2 text-xs text-stone-500">
          <div>{conversationId ? `Conversation #${conversationId.slice(0, 8)}` : 'New conversation'}</div>
          <div className="flex items-center gap-2">
            <Button onClick={sendMessage} disabled={loading}>
              {loading ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </div>
        {error && <div className="mt-2 text-red-600 text-xs">{error}</div>}
      </div>
    </div>
  );
};

export default ChatWidget;
